/**
 * PWA-03 «Первый запуск установленного Web App» (CLIENT_IOS_WEBAPP.md §2.3, §9).
 *
 * Показывается один раз поверх первого экрана после входа в standalone-режиме
 * на iPhone: короткое приветствие и ОТДЕЛЬНОЕ действие «Включить уведомления»,
 * которое открывает N-03. Системный запрос разрешения отсюда не выполняется
 * (ПС §16 п. 6, §18 «Правило разрешений»): на iOS он допустим только внутри
 * обработчика жеста, и N-03 — единственное место, где этот жест есть.
 *
 * Отметка показа живёт в реплике (`UiKey.seenHints`, ключ `pwa_welcome`):
 * она исчезает вместе с базой при выходе и смене владельца сессии, как и
 * требует §9 («Локальные данные» PWA-03), а в `localStorage` ей места нет
 * (CLIENT_WEB.md §1.4).
 */
import { Link } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { usePushPermission } from '@features/push/use-push'
import { hintsRepo } from '@shared/db'
import { launchMode } from '@shared/pwa/platform'
import { Button, Dialog, Icon } from '@shared/ui'

import { useAppStore } from '../../model/app-store'
import styles from './welcome.module.css'

export function StandaloneWelcomeHost(): React.ReactElement | null {
  const phase = useAppStore((s) => s.phase)
  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  const blocking = useAppStore((s) => s.blocking)
  const pushPromptOpen = useAppStore((s) => s.pushPromptOpen)
  const online = useAppStore((s) => s.online)
  const openPushPrompt = useAppStore((s) => s.openPushPrompt)

  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const push = usePushPermission(config)

  /*
   * §9 PWA-03 «Маршрут / точка входа»: раскладка standalone на iOS, сессия
   * валидна и аккаунт `active`, onboarding завершён, `GET /config` получен.
   * Без сессии окно недостижимо — клиент идёт на A-01 → A-02 по SYS-17.
   */
  const userId = me?.user.id ?? null
  const eligible =
    launchMode() === 'installed_web_app' &&
    phase === 'ready' &&
    !blocking &&
    config !== null &&
    me?.user.status === 'active' &&
    me.onboarding.complete

  useEffect(() => {
    if (!eligible || !userId) return

    let cancelled = false
    void hintsRepo
      .wasSeen('pwa_welcome')
      .then((seen) => {
        if (!cancelled && !seen) setOpen(true)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [eligible, userId])

  // §2.3 п. 5: любое закрытие — отметка «показан», повторно окно не появляется.
  const close = useCallback(() => {
    setOpen(false)
    setError(null)
    void hintsRepo.markSeen('pwa_welcome').catch(() => undefined)
  }, [])

  const enable = (): void => {
    /*
     * §9 PWA-03 «Состояния → Офлайн»: локальная ошибка без системного диалога,
     * чтобы не потратить единственную попытку платформы впустую.
     */
    if (!online) {
      setError('Нет соединения. Включить уведомления можно позже — в настройках.')
      return
    }

    // Синхронно, в том же обработчике нажатия: N-03 и системный запрос из него.
    close()
    openPushPrompt()
  }

  if (!open || pushPromptOpen) return null

  const canEnable = push.state === 'prompt'

  return (
    <Dialog
      open
      onClose={close}
      title="Добро пожаловать в «Движ»"
      description="Приложение установлено и запускается с экрана «Домой» — как обычное."
      footer={
        canEnable ? (
          <>
            <Button variant="secondary" onClick={close}>
              Не сейчас
            </Button>
            <Button onClick={enable}>Включить уведомления</Button>
          </>
        ) : (
          <Button onClick={close}>Продолжить</Button>
        )
      }
    >
      <div className={styles['hero']}>
        <span className={styles['mark']} aria-hidden="true">
          <Icon name="matches" size={28} />
        </span>
        <p className={styles['lead']}>
          Новые сообщения и совпадения видны на вкладках и в центре уведомлений — даже без системных
          уведомлений.
        </p>
      </div>

      {canEnable ? (
        <ul className={styles['benefits']}>
          <li>
            <Icon name="chats" size={18} />
            <span>Новое сообщение — когда приложение закрыто</span>
          </li>
          <li>
            <Icon name="heart" size={18} />
            <span>Новое совпадение — сразу, а не при следующем заходе</span>
          </li>
          <li>
            <Icon name="smile" size={18} />
            <span>Реакции на ваши сообщения</span>
          </li>
        </ul>
      ) : (
        <PushUnavailableNote state={push.state} />
      )}

      {error ? (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      ) : null}
    </Dialog>
  )
}

/**
 * §9 PWA-03 «Что видит пользователь»: при отключённом на сервере канале или
 * недоступном Web Push кнопки включения нет — пояснение и путь к N-04 (S-04).
 * Тексты статуса — по CLIENT_IOS_WEBAPP.md §5.2 (последний абзац).
 */
function PushUnavailableNote({ state }: { state: ReturnType<typeof usePushPermission>['state'] }) {
  switch (state) {
    case 'granted':
      return <p className={styles['note']}>Уведомления уже включены — всё готово.</p>

    case 'denied':
      return (
        <p className={styles['note']}>
          Уведомления запрещены в настройках iOS: «Настройки» → «Уведомления» → «Движ». Непрочитанное
          по-прежнему видно внутри приложения.
        </p>
      )

    case 'unsupported':
      return (
        <p className={styles['note']}>
          Системные уведомления недоступны в этой версии iOS — они появятся после обновления до iOS 16.4 или
          новее. Непрочитанное по-прежнему видно внутри приложения.
        </p>
      )

    case 'server_disabled':
    default:
      return (
        <p className={styles['note']}>
          Уведомления пока недоступны. Когда они появятся, включить их можно будет в{' '}
          <Link to="/settings/$section" params={{ section: 'notifications' }}>
            настройках уведомлений
          </Link>
          .
        </p>
      )
  }
}
